import { useEffect } from "react";
import { withTrailingSlash } from "@/lib/urls";

type SchemaObject = Record<string, unknown>;

type SeoProps = {
  title: string;
  description: string;
  canonicalPath?: string;
  type?: "website" | "article";
  schema?: SchemaObject | SchemaObject[];
  robots?: string;
};

const setMeta = (selector: string, attribute: "content" | "href", value: string) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;

  if (!element) {
    if (selector.startsWith("meta")) {
      element = document.createElement("meta");
      const nameMatch = selector.match(/name="([^"]+)"/);
      const propertyMatch = selector.match(/property="([^"]+)"/);
      if (nameMatch) element.setAttribute("name", nameMatch[1]);
      if (propertyMatch) element.setAttribute("property", propertyMatch[1]);
    } else {
      element = document.createElement("link");
      element.setAttribute("rel", "canonical");
    }
    document.head.appendChild(element);
  }

  element.setAttribute(attribute, value);
};

export function Seo({
  title,
  description,
  canonicalPath = "/",
  type = "website",
  schema,
  robots = "index, follow",
}: SeoProps) {
  useEffect(() => {
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${withTrailingSlash(canonicalPath)}`;
    const fullTitle = title.includes("United Calculator") ? title : `${title} | United Calculator`;

    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:site_name"]', "content", "United Calculator");
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:image"]', "content", `${origin}/og-image.svg`);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", `${origin}/og-image.svg`);
    setMeta('meta[name="robots"]', "content", robots);
    setMeta('link[rel="canonical"]', "href", canonicalUrl);

    document.head.querySelectorAll('script[data-united-calculator-schema="true"]').forEach((element) => element.remove());
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((schemaItem) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.unitedCalculatorSchema = "true";
        script.textContent = JSON.stringify(schemaItem);
        document.head.appendChild(script);
      });
    }
  }, [canonicalPath, description, robots, schema, title, type]);

  return null;
}
